/**
 * Shared helpers for resolving *when* a visit was completed.
 *
 * A visit spans a window of days on the calendars (see `period`), but it is
 * actually performed on a single day. Before these helpers, `status: true`
 * painted every day of the window as "completed", so a 60-day window looked
 * like 60 completed days and nobody could tell when the visit really happened.
 *
 * Source of truth, in priority order:
 *   1. `visitEndDate` / `visitEndTime` — the real end of the visit, entered on
 *      the form. Required for home visits.
 *   2. `completedAt` — ISO timestamp of when the visit was marked complete.
 *      Covers hospital/site visits, where the end date is not mandatory.
 *
 * Visits completed before `completedAt` existed have neither, so they fall back
 * to the legacy "whole window is green" rendering rather than silently losing
 * their completed state.
 */

/** Day states for a completed visit. `null` means the visit is not completed. */
export const COMPLETION_STATE = {
  /** This is the day the visit was actually performed. */
  COMPLETED_DAY: 'completedDay',
  /** Part of the window, but not the day it happened — the window has closed. */
  WINDOW_CLOSED: 'windowClosed',
  /** Completed, but no completion date was ever recorded (legacy data). */
  COMPLETED_UNKNOWN: 'completedUnknown',
};

const MONTH_NAMES_SHORT = [
  'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
  'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara',
];

/** Strip the time part so two dates can be compared by calendar day. */
export const normalizeToDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Parse a "YYYY-MM-DD" value from a date input as a *local* date.
 * `new Date('2026-07-30')` is parsed as UTC midnight, which shifts to the
 * previous day in negative offsets — this avoids that off-by-one.
 */
const parseDateInput = (value) => {
  if (!value || typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(d.getTime()) ? null : d;
};

/** Parse a Firestore Timestamp, Date, or ISO string. */
const parseTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * The moment the visit was completed, with time where available.
 * Returns `null` for visits that are not completed, or completed without a
 * recorded date.
 */
export const getVisitCompletionMoment = (visit) => {
  if (!visit || !visit.status) return null;

  const endDate = parseDateInput(visit.visitEndDate);
  if (endDate) {
    const time = typeof visit.visitEndTime === 'string'
      ? visit.visitEndTime.match(/^(\d{1,2}):(\d{2})/)
      : null;
    if (time) {
      endDate.setHours(Number(time[1]), Number(time[2]), 0, 0);
    }
    return endDate;
  }

  return parseTimestamp(visit.completedAt);
};

/** The calendar day the visit was completed on, or `null` if unknown. */
export const getVisitCompletionDate = (visit) => normalizeToDay(getVisitCompletionMoment(visit));

/**
 * Classify a single calendar day for a completed visit.
 * Returns one of `COMPLETION_STATE`, or `null` when the visit is not completed
 * (in which case callers should fall back to their planned/overdue logic).
 */
export const getCompletionState = (visit, date) => {
  if (!visit || !visit.status) return null;

  const completionDay = getVisitCompletionDate(visit);
  if (!completionDay) return COMPLETION_STATE.COMPLETED_UNKNOWN;

  const day = normalizeToDay(date);
  if (!day) return COMPLETION_STATE.WINDOW_CLOSED;

  return day.getTime() === completionDay.getTime()
    ? COMPLETION_STATE.COMPLETED_DAY
    : COMPLETION_STATE.WINDOW_CLOSED;
};

/** `true` only on the day the visit was actually performed. */
export const isVisitCompletionDay = (visit, date) =>
  getCompletionState(visit, date) === COMPLETION_STATE.COMPLETED_DAY;

/**
 * Completion date for display, e.g. "30.07.2026" or "30.07.2026 14:20".
 * Returns an empty string when there is nothing to show.
 */
export const formatCompletionDate = (visit, { withTime = false } = {}) => {
  const moment = getVisitCompletionMoment(visit);
  if (!moment) return '';

  const day = String(moment.getDate()).padStart(2, '0');
  const month = String(moment.getMonth() + 1).padStart(2, '0');
  const formatted = `${day}.${month}.${moment.getFullYear()}`;

  if (!withTime) return formatted;

  // A bare `completedAt` always carries a time; a date-only `visitEndDate`
  // without `visitEndTime` lands on midnight, which is not worth showing.
  const hasTime = moment.getHours() !== 0 || moment.getMinutes() !== 0;
  if (!hasTime) return formatted;

  const hours = String(moment.getHours()).padStart(2, '0');
  const minutes = String(moment.getMinutes()).padStart(2, '0');
  return `${formatted} ${hours}:${minutes}`;
};

/** Compact completion date for tight spaces (week cards), e.g. "30 Tem". */
export const formatCompletionDateShort = (visit) => {
  const moment = getVisitCompletionMoment(visit);
  if (!moment) return '';
  return `${moment.getDate()} ${MONTH_NAMES_SHORT[moment.getMonth()]}`;
};

/**
 * The "D — Tamamlanma Tarihleri" dates of a hastane visit.
 *
 * None of them block completing a visit: chasing e-CRF entries, source
 * documents and signatures takes weeks, and requiring them left finished visits
 * stuck open. Whatever is still missing at completion is recorded on the visit
 * as `pendingCompletionFields` and surfaced until it arrives.
 */
export const COMPLETION_DATE_FIELDS = [
  { key: 'ecrf', field: 'ecrfCompletionDate', label: 'e-CRF' },
  { key: 'sourceDoc', field: 'sourceDocCompletionDate', label: 'Kaynak döküman' },
  { key: 'signature', field: 'signatureCompletionDate', label: 'e-CRF imzası' },
];

/** Keys of the D-section dates that are not filled in yet. */
export const getMissingCompletionKeys = (visitEvaluation) =>
  COMPLETION_DATE_FIELDS
    .filter(({ field }) => !visitEvaluation?.[field])
    .map(({ key }) => key);

/**
 * Labels of the D-section dates a completed visit is still waiting for.
 *
 * Both the flag stored at completion *and* the live data have to agree, so
 * filling a date in clears its warning even if the stored list is stale, and a
 * visit is never retroactively marked incomplete for a date it was never asked
 * for.
 */
export const getPendingCompletionLabels = (visit) => {
  if (!visit?.status) return [];

  const stored = Array.isArray(visit.pendingCompletionFields)
    ? visit.pendingCompletionFields
    // Visits completed before the section was generalised carried a single flag.
    : (visit.signaturePending ? ['signature'] : []);
  if (stored.length === 0) return [];

  const missingNow = getMissingCompletionKeys(visit.visitEvaluation);

  return COMPLETION_DATE_FIELDS
    .filter(({ key }) => stored.includes(key) && missingNow.includes(key))
    .map(({ label }) => label);
};

/** `true` when a completed visit is still missing one of the D-section dates. */
export const isCompletionPending = (visit) =>
  getPendingCompletionLabels(visit).length > 0;

/**
 * Tooltip line describing the completion, from the perspective of `date`.
 * Returns an empty string for visits that are not completed.
 */
export const getCompletionTooltipLine = (visit, date) => {
  if (!visit || !visit.status) return '';

  const pendingLabels = getPendingCompletionLabels(visit);
  const pendingNote = pendingLabels.length > 0
    ? ` · Eksik: ${pendingLabels.join(', ')}`
    : '';

  const formatted = formatCompletionDate(visit, { withTime: true });
  if (!formatted) return `Tamamlandı · Tamamlanma tarihi kayıtlı değil${pendingNote}`;

  // No date means the caller is describing the visit as a whole (e.g. the month
  // view, which renders a single pill per visit) rather than one of its days.
  if (!date) return `Tamamlandı: ${formatted}${pendingNote}`;

  return isVisitCompletionDay(visit, date)
    ? `Bu gün tamamlandı: ${formatted}${pendingNote}`
    : `Tamamlandı: ${formatted} · Pencere kapandı${pendingNote}`;
};
